import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Search,
  Calendar,
  Home as HomeIcon,
  Stethoscope,
  Heart,
  Brain,
  Baby,
  Eye,
  Bone,
  Activity,
  Star,
  ChevronRight,
  Phone,
  MapPin,
  Clock,
  CheckCircle,
  Menu,
  X,
  Mail,
  Shield,
  BadgeCheck,
  Headphones,
  Lock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

const specializations = [
  { id: 'general', name: 'General', icon: Stethoscope },
  { id: 'cardiology', name: 'Cardiology', icon: Heart },
  { id: 'neurology', name: 'Neurology', icon: Brain },
  { id: 'pediatrics', name: 'Pediatrics', icon: Baby },
  { id: 'ophthalmology', name: 'Ophthalmology', icon: Eye },
  { id: 'orthopedics', name: 'Orthopedics', icon: Bone },
 
];

const services = [
  {
    id: 'find_doctor',
    title: 'Find Doctor',
    description: 'Search verified doctors by specialization, location and availability. Book appointments with top-rated doctors near you.',
    icon: Stethoscope,
    color: 'bg-blue-500',
    link: '/search?role=doctor',
  },
  {
    id: 'find_nurse',
    title: 'Find Nurse',
    description: 'Find experienced nurses for home care, night duty, post-surgery care, elderly care and more at your doorstep.',
    icon: Heart,
    color: 'bg-rose-500',
    link: '/search?role=nurse',
  },
  {
    id: 'home_visit',
    title: 'Home Visit',
    description: 'Get doctors or nurses to visit you at home for consultation, checkups and care.',
    icon: HomeIcon,
    color: 'bg-emerald-500',
    link: '/search?service=home_visit',
  },
];

const steps = [
  { number: '01', title: 'Search', description: 'Find doctors, nurses near you' },
  { number: '02', title: 'Book', description: 'Choose your preferred time slot' },
  { number: '03', title: 'Consult', description: 'Get the care you need' },
];

const testimonials = [
  {
    name: 'Raj',
    role: 'Patient',
    content: 'Physign made it so easy to find a specialist. The home visit service is incredibly convenient!',
    rating: 5,
  },
  {
    name: 'Dr. Prem',
    role: 'Cardiologist',
    content: 'Great platform to connect with patients. The scheduling system is intuitive and efficient.',
    rating: 5,
  },
  {
    name: 'Priya Sharma',
    role: 'Patient',
    content: 'Excellent service! Got my lab tests done at home without any hassle.',
    rating: 5,
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const handleCategoryClick = (specialization) => {
    navigate(`/search?specialization=${specialization}`);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <SEO
        path="/"
        title="Book Doctors, Nurses & Home Healthcare"
        description="India's trusted healthcare platform. Book verified doctors, nurses, and home healthcare in minutes. Same-day appointments, transparent pricing, home visits & online consultations."
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="container-main">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
             {/* <img src={PHYSIGN_LOGO} alt="Physign" className="w-10 h-10 rounded-xl object-cover" />
*/}              <span className="text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Physign
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/book-appointment" className="nav-link" data-testid="nav-book-appointment">Book Appointment</Link>
              <Link to="/doctors" className="nav-link" data-testid="nav-doctors">Find Doctors</Link>
              <Link to="/nurses" className="nav-link" data-testid="nav-nurses">Find Nurses</Link>
              <Link to="/home-visit" className="nav-link" data-testid="nav-home-visit">Home Visit</Link>

              {/*<Link to="/search?role=lab" className="nav-link" data-testid="nav-labs">Lab Tests</Link>*/}

            </div>

            <div className="hidden md:flex items-center gap-4">
              {isAuthenticated ? (
                <Button 
                  onClick={() => navigate(user?.role === 'admin' ? '/physign-admin-panel' : '/dashboard')} 
                  className="bg-[#0F62FE] hover:bg-[#0050E6] text-white rounded-lg"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/auth')}
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => navigate('/auth?mode=signup')} 
                    className="bg-[#0F62FE] hover:bg-[#0050E6] text-white rounded-lg"
                    data-testid="signup-btn"
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4">
            <div className="container-main space-y-4">
              <Link to="/book-appointment" className="block py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)}>Book Appointment</Link>
              <Link to="/doctors" className="block py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)}>Find Doctors</Link>
              <Link to="/nurses" className="block py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)}>Find Nurses</Link>
              <Link to="/home-visit" className="block py-2 text-slate-600" onClick={() => setMobileMenuOpen(false)}>Home Visit</Link>
             
              <div className="pt-4 border-t border-slate-100">
                {isAuthenticated ? (
                  <Button 
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className="w-full bg-[#0F62FE] text-white"
                  >
                    Dashboard
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
                    >
                      Login
                    </Button>
                    <Button 
                      className="w-full bg-[#0F62FE] text-white"
                      onClick={() => { navigate('/auth?mode=signup'); setMobileMenuOpen(false); }}
                    >
                      Sign Up
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 hero-gradient">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Your Health,{' '}
                <span className="text-[#0F62FE]">Our Priority</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                Connect with verified doctors, nurses . Book appointments, schedule home visits.
              </p>

              {/* Search Box */}
              <form onSubmit={handleSearch} className="mt-8 search-box max-w-xl" data-testid="search-form">
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Search doctors, specializations, labs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-12 border-slate-200"
                      data-testid="search-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="h-12 px-6 bg-[#0F62FE] hover:bg-[#0050E6] text-white rounded-lg"
                    data-testid="search-btn"
                  >
                    Search
                  </Button>
                </div>
              </form>
              {/* Stats */}
              {/*
              <div className="mt-10 flex gap-8">
              <div>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>50+</p>
              <p className="text-sm text-slate-500">Verified Doctors</p>
              </div>
              <div>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>1K+</p>
              <p className="text-sm text-slate-500">Happy Patients</p>
              </div>
              <div>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>5+</p>
              <p className="text-sm text-slate-500">Lab Partners</p>
              </div>
              </div>
              </div>
              */}
            </div>

            <div className="hidden lg:block animate-fade-in stagger-2">
              <img
                src="https://images.pexels.com/photos/7578808/pexels-photo-7578808.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Doctor consulting patient"
                className="rounded-2xl shadow-2xl w-full object-cover max-h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Our Services
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              Quality healthcare services tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card 
                key={service.id} 
                className="service-card cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(service.link)}
                data-testid={`service-${service.id}`}
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-xl ${service.color} flex items-center justify-center mb-4`}>
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-slate-600">{service.description}</p>
                  <div className="mt-4 flex items-center text-[#0F62FE] font-medium">
                    Learn more <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              How It Works
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Book an appointment in 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div 
                key={step.number} 
                className="text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="w-16 h-16 rounded-full bg-[#E0F2FE] flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#0F62FE]">{step.number}</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Why Choose Physign
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              We are committed to providing the best healthcare experience
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="why-choose-section">
            <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200/60 text-center animate-fade-in" data-testid="trust-verified">
              <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center mx-auto mb-4">
                <BadgeCheck className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Verified Providers</h3>
              <p className="text-sm text-slate-600">Every doctor and nurse on our platform is verified with valid medical registration and documents.</p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/60 text-center animate-fade-in" style={{animationDelay:'0.1s'}} data-testid="trust-secure">
              <div className="w-14 h-14 rounded-xl bg-blue-500 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Secure & Confidential</h3>
              <p className="text-sm text-slate-600">Your health data and personal information are protected with industry-standard encryption.</p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-violet-50 to-violet-100/50 border border-violet-200/60 text-center animate-fade-in" style={{animationDelay:'0.2s'}} data-testid="trust-booking">
              <div className="w-14 h-14 rounded-xl bg-violet-500 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Easy Online Booking</h3>
              <p className="text-sm text-slate-600">Book appointments in just 3 clicks. Choose clinic visit or home visit as per your convenience.</p>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200/60 text-center animate-fade-in" style={{animationDelay:'0.3s'}} data-testid="trust-support">
              <div className="w-14 h-14 rounded-xl bg-amber-500 flex items-center justify-center mx-auto mb-4">
                <Headphones className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">24/7 Support</h3>
              <p className="text-sm text-slate-600">Our dedicated support team is available round the clock to assist you with any queries.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Specialization */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Browse by Specialization
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Find the right specialist for your needs
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {specializations.map((spec, index) => (
              <div
                key={spec.id}
                className="category-card animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => handleCategoryClick(spec.id)}
                data-testid={`category-${spec.id}`}
              >
                <div className="category-icon">
                  <spec.icon className="w-6 h-6" />
                </div>
                <span className="text-sm font-medium text-slate-700">{spec.name}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button 
              variant="outline" 
              className="rounded-lg"
              onClick={() => navigate('/search')}
              data-testid="view-all-btn"
            >
              View All Specializations <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              What People Say
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Trusted by thousands of patients and healthcare providers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="testimonial-card animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="testimonial-quote mb-4">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="avatar">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#0F62FE]">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Ready to Get Started?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of patients and healthcare providers on Physign. Your health journey starts here.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-[#0F62FE] hover:bg-slate-100 rounded-lg px-8"
              onClick={() => navigate('/auth?mode=signup')}
              data-testid="cta-patient-btn"
            >
              Book Appointment
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 rounded-lg px-8"
              onClick={() => navigate('/provider-signup')}
              data-testid="cta-provider-btn"
            >
              Join as Provider
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-white">
        <div className="container-main">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>Physign</span>
              </div>
              <p className="text-slate-400 text-sm">
                Connecting patients with verified healthcare providers for quality care.
              </p>
              <div className="flex gap-3 mt-4">
                <a href="https://www.linkedin.com/company/physign/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-[#0A66C2] flex items-center justify-center transition-colors" data-testid="social-linkedin">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href="https://www.instagram.com/withphysign?igsh=OW9mZHYwN25sbjNk&utm_source=qr" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] flex items-center justify-center transition-colors" data-testid="social-instagram">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/search" className="footer-link">Book Appointment</Link></li>
                <li><Link to="/search?role=doctor" className="footer-link">Find Doctors</Link></li>
                <li><Link to="/search?role=nurse" className="footer-link">Find Nurses</Link></li>
                <li><Link to="/search?service=home_visit" className="footer-link">Home Visits</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/about" className="footer-link">About Us</Link></li>
                <li><Link to="/faq" className="footer-link">FAQ</Link></li>
                <li><Link to="/terms" className="footer-link">Terms & Conditions</Link></li>
                <li><Link to="/privacy-policy" className="footer-link">Privacy Policy</Link></li>
                <li><a href="#" className="footer-link">Careers</a></li>
                <li><Link to="/contact" className="footer-link">Contact</Link></li>
                <li><Link to="/blog" className="footer-link" data-testid="footer-blog-link">Blog</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-3 text-slate-400 text-sm">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#0F62FE]" /> 
                  <a href="tel:+919205596140" className="hover:text-white transition-colors">+91 9205596140</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#0F62FE]" /> 
                  <a href="mailto:support@physign.com" className="hover:text-white transition-colors">support@physign.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#0F62FE]" /> 
                  <span>24/7 Customer Support</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © 2026 Physign Technologies Private Limited. All rights reserved.
            </p>
            <div className="flex gap-6 text-slate-400 text-sm">
              <Link to="/privacy-policy" className="footer-link" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/terms" className="footer-link" data-testid="footer-terms-link">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
